/**
 * Contextual Modify tab — action registry.
 *
 * Composes every one-shot contextual action backend into a flat `ctx.<name>`
 * map consumed by CommandRegistrar (registered under the same ids the ribbon
 * config references). Interactive editors (sketch/profile/sub-element/host
 * pickers) are viewport tools registered in useToolController instead; the
 * family-level actions (Edit Family, Create Similar, Create Group, Cut/Copy/
 * Paste) already exist in useContextMenuActions and are reused as-is.
 */
import { useMemo } from 'react';
import { useWallContextualActions, useSurfaceContextualActions, useViewContextualActions } from './contextual/wallSurfaceActions';
import {
    useStairContextualActions,
    useDatumContextualActions,
    useAnnotationContextualActions,
    useGroupContextualActions,
    useSelectionBoxAction,
} from './contextual/datumGroupActions';
import { useImportContextualActions } from './contextual/importActions';

export interface ContextualActions {
    // Walls
    ctx_resetProfile: () => void;
    ctx_attachTop: () => void;
    ctx_attachBase: () => void;
    ctx_detachTop: () => void;
    ctx_detachBase: () => void;
    ctx_wallJoins: () => void;
    // Floors / surfaces
    ctx_resetShape: () => void;
    // View panel
    ctx_selectionBox: () => void;
    ctx_createParts: () => void;
    ctx_createAssembly: () => void;
    // Stairs
    ctx_flipStairDirection: () => void;
    ctx_resetStair: () => void;
    // Datum
    ctx_maximizeExtents: () => void;
    ctx_propagateExtents: () => void;
    ctx_reset3DExtents: () => void;
    // Annotations
    ctx_dimensionEquality: () => void;
    ctx_textLeaderLeft: () => void;
    ctx_textLeaderRight: () => void;
    ctx_textLeaderNone: () => void;
    ctx_textAlignLeft: () => void;
    ctx_textAlignCenter: () => void;
    ctx_textAlignRight: () => void;
    // Groups
    ctx_editGroup: () => void;
    ctx_ungroup: () => void;
    ctx_linkGroup: () => void;
    // Imports
    ctx_explodeFull: () => void;
    ctx_deleteLayers: () => void;
    ctx_foreground: () => void;
    ctx_background: () => void;
}

/**
 * Build the full contextual action set. All backends read live stores at call
 * time (getState), so the returned functions stay referentially stable and
 * never need re-subscription.
 */
export function useContextualActions(): ContextualActions {
    const walls = useWallContextualActions();
    const surfaces = useSurfaceContextualActions();
    const views = useViewContextualActions();
    const stairs = useStairContextualActions();
    const datum = useDatumContextualActions();
    const annotations = useAnnotationContextualActions();
    const groups = useGroupContextualActions();
    const imports = useImportContextualActions();
    const selection = useSelectionBoxAction();

    // All backends are useCallback-stable; memoize the aggregate so consumers
    // (CommandRegistrar) never re-register on unrelated renders.
    return useMemo(() => ({
        ctx_resetProfile: walls.resetProfile,
        ctx_attachTop: walls.attachTop,
        ctx_attachBase: walls.attachBase,
        ctx_detachTop: walls.detachTop,
        ctx_detachBase: walls.detachBase,
        ctx_wallJoins: walls.wallJoins,
        ctx_resetShape: surfaces.resetShape,
        ctx_selectionBox: selection.selectionBox,
        ctx_createParts: views.createParts,
        ctx_createAssembly: views.createAssembly,
        ctx_flipStairDirection: stairs.flipStairDirection,
        ctx_resetStair: stairs.resetStair,
        ctx_maximizeExtents: datum.maximizeExtents,
        ctx_propagateExtents: datum.propagateExtents,
        ctx_reset3DExtents: datum.reset3DExtents,
        ctx_dimensionEquality: annotations.dimensionEquality,
        ctx_textLeaderLeft: annotations.textLeaderLeft,
        ctx_textLeaderRight: annotations.textLeaderRight,
        ctx_textLeaderNone: annotations.textLeaderNone,
        ctx_textAlignLeft: annotations.textAlignLeft,
        ctx_textAlignCenter: annotations.textAlignCenter,
        ctx_textAlignRight: annotations.textAlignRight,
        ctx_editGroup: groups.editGroup,
        ctx_ungroup: groups.ungroup,
        ctx_linkGroup: groups.linkGroup,
        ctx_explodeFull: imports.explodeFull,
        ctx_deleteLayers: imports.deleteLayers,
        ctx_foreground: imports.foreground,
        ctx_background: imports.background,
    // Every sub-action is a useCallback-stable closure that reads stores at
    // call time, so the aggregate never needs to rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);
}
